import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import cx from 'classnames';
import { AsyncThunk } from '@reduxjs/toolkit';
import { partition } from 'lodash';

import {
  CustomerAgreementFileParams,
  getServiceAgreementFileInfoForCustomer,
} from 'redux/reducers/clientSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { downloadFile } from 'utils/fileUtils';
import {
  ExtendedNamedAttachmentModel,
  ResourceDownloadResponse,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import BaseAlert from 'components/wrappers/BaseAlert';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  getAgreementButtonLabel,
  mapAgreementFilesLoadStatus,
  updateFileStatus,
} from './agreementUtils';
import {
  getRecruiterPriceAgreementInfoFile,
  RecruiterPriceAgreementFileParams,
} from 'redux/reducers/recruiterSlice';

import styles from './DownloadClientAgreement.module.less';

export enum FileLoadingStatus {
  Default = 1,
  Success = 2,
  Loading = 3,
  Failed = 4,
}

export interface FileLoadStatus {
  fileName: string;
  fileKey: string;
  status?: FileLoadingStatus;
}

export enum DownloadAgreementTypeEnum {
  Customer = 1,
  Member = 2,
}

type Props = {
  id?: string;
  listOfFiles?: ExtendedNamedAttachmentModel[];
  agreementDetailsType?: ServiceAgreementTypeEnum;
  downloadAgreementType?: DownloadAgreementTypeEnum;
};

export default function DownloadClientAgreement(props: Props) {
  const {
    listOfFiles = [],
    id,
    agreementDetailsType,
    downloadAgreementType,
  } = props;
  const [fileLoadStatuses, setFileLoadStatuses] = useState<FileLoadStatus[]>(
    mapAgreementFilesLoadStatus(listOfFiles),
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const isAdminRole = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setFileLoadStatuses(mapAgreementFilesLoadStatus(listOfFiles));
  }, [listOfFiles]);

  useEffect(() => {
    return () => {
      timerId && clearTimeout(timerId);
    };
  }, [timerId]);

  const downloadClientFile = async (
    downloadResponse: ResourceDownloadResponse,
  ) => {
    try {
      const { downloadUrl, fileName } = downloadResponse;

      await downloadFile({ downloadUrl, fileName });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  const loadFileInfo = useCallback(
    async (fileKey: string, fileName: string) => {
      let requestData:
        | CustomerAgreementFileParams
        | RecruiterPriceAgreementFileParams;
      let correctAction: AsyncThunk<ResourceDownloadResponse, any, {}>;

      switch (downloadAgreementType) {
        case DownloadAgreementTypeEnum.Customer:
          correctAction = getServiceAgreementFileInfoForCustomer;
          requestData = {
            customerId: id as string,
            isAdminRole: isAdminRole,
            fileKey,
            fileName,
          };
          break;
        case DownloadAgreementTypeEnum.Member:
        default:
          correctAction = getRecruiterPriceAgreementInfoFile;
          requestData = {
            userId: id as string,
            fileKey,
            fileName,
            isAdminRole: isAdminRole,
          };
      }

      const result = await dispatch(correctAction(requestData));

      if (correctAction.fulfilled.match(result)) {
        return result.payload;
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);

        return Promise.reject();
      }
    },
    [id, dispatch, isAdminRole, downloadAgreementType],
  );

  const handleDownloadAgreement = useCallback(
    async (filesInfo: FileLoadStatus[]) => {
      const fileKeys = filesInfo.map((file) => file.fileKey);

      setFileLoadStatuses(
        updateFileStatus(FileLoadingStatus.Loading, fileKeys),
      );

      const responses = await Promise.allSettled(
        filesInfo.map((file) => loadFileInfo(file.fileKey, file.fileName)),
      );

      const [fulfilledResponses, rejectedResponses] = partition(
        responses,
        (response) => response.status === 'fulfilled',
      );

      fulfilledResponses.length > 0 &&
        setFileLoadStatuses(
          updateFileStatus(
            FileLoadingStatus.Success,
            fulfilledResponses.map((response) => response.value.fileKey),
          ),
        );

      rejectedResponses.length > 0 &&
        setFileLoadStatuses(
          updateFileStatus(
            FileLoadingStatus.Failed,
            rejectedResponses.map((response) => response.value.fileKey),
          ),
        );

      Promise.allSettled(
        fulfilledResponses.map((response) =>
          downloadClientFile(response.value),
        ),
      );

      const timer = setTimeout(() => {
        setFileLoadStatuses(
          updateFileStatus(FileLoadingStatus.Default, fileKeys),
        );
      }, 5000);

      setTimerId(timer);
    },
    [loadFileInfo],
  );

  const createFileMenu = (files: FileLoadStatus[]) => {
    const items: ItemType[] = [
      {
        key: 'downloadAll',
        onClick: () => handleDownloadAgreement(files),
        label: (
          <div className={cx(styles.text, styles.all)}>
            Download All <span className="icon icon-download" />
          </div>
        ),
      },
    ];

    const dynamicItems: ItemType[] = files.map((file) => {
      const status = fileLoadStatuses.find((f) => f.fileKey === file.fileKey)
        ?.status;

      let icon;
      switch (status) {
        case FileLoadingStatus.Success:
          icon = <i className={cx('icon icon-check-filled', styles.success)} />;
          break;
        case FileLoadingStatus.Loading:
          icon = <i className={cx('icon icon-loader', styles.loading)} />;
          break;
        case FileLoadingStatus.Failed:
          icon = <i className={cx('icon icon-closed-filled', styles.failed)} />;
          break;
        default:
          icon = <i className="icon icon-download" />;
          break;
      }

      return {
        key: `file-${file.fileKey}`,
        icon: <i className="icon icon-paperclip" />,
        onClick: () => handleDownloadAgreement([file]),
        label: (
          <div className={styles.text}>
            {file.fileName}
            {icon}
          </div>
        ),
      } as ItemType;
    });

    return (
      <Menu items={[...items, ...dynamicItems]} className={styles.dropdown} />
    );
  };

  if (!listOfFiles) return null;

  return (
    <Dropdown
      overlay={createFileMenu(fileLoadStatuses)}
      placement="top"
      open={dropdownVisible}
      onOpenChange={setDropdownVisible}
    >
      <Button
        className="with-icon"
        icon={<i className="icon icon-paperclip" />}
      >
        {getAgreementButtonLabel(agreementDetailsType)}
      </Button>
    </Dropdown>
  );
}
